import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export interface TimeRange {
  startTime: number;
  endTime: number;
  rangeType: 'preset' | 'custom';
  presetValue?: string;
}

interface TimeRangeSelectorProps {
  value: TimeRange;
  onChange: (range: TimeRange) => void;
  className?: string;
}

export default function TimeRangeSelector({ value, onChange, className = '' }: TimeRangeSelectorProps) {
  const { t } = useTranslation('common');

  const PRESET_RANGES = [
    { value: '15m', label: t('timeRanges.last15m'), ms: 15 * 60 * 1000 },
    { value: '1h', label: t('timeRanges.last1h'), ms: 60 * 60 * 1000 },
    { value: '6h', label: t('timeRanges.last6h'), ms: 6 * 60 * 60 * 1000 },
    { value: '12h', label: t('timeRanges.last12h'), ms: 12 * 60 * 60 * 1000 },
    { value: '24h', label: t('timeRanges.last24h'), ms: 24 * 60 * 60 * 1000 },
    { value: '7d', label: t('timeRanges.last7d'), ms: 7 * 24 * 60 * 60 * 1000 },
    { value: '30d', label: t('timeRanges.last30d'), ms: 30 * 24 * 60 * 60 * 1000 },
  ];
  const [mode, setMode] = useState<'preset' | 'custom'>('preset');
  const [presetValue, setPresetValue] = useState('1h');
  const [startDateTime, setStartDateTime] = useState('');
  const [endDateTime, setEndDateTime] = useState('');

  // Helper to format date for datetime-local input (local timezone)
  const formatDateTimeLocalFromTimestamp = (timestamp: number): string => {
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Initialize from value (only on mount)
  useEffect(() => {
    if (value && value.rangeType === 'preset' && value.presetValue) {
      setMode('preset');
      setPresetValue(value.presetValue);
    } else if (value && value.rangeType === 'custom') {
      setMode('custom');
      const startLocal = formatDateTimeLocalFromTimestamp(value.startTime);
      const endLocal = formatDateTimeLocalFromTimestamp(value.endTime);

      // Only update if different to avoid loops
      setStartDateTime(prev => prev !== startLocal ? startLocal : prev);
      setEndDateTime(prev => prev !== endLocal ? endLocal : prev);
    }
  }, []); // Empty dependency array - only run on mount

  const handlePresetChange = (newPreset: string) => {
    setPresetValue(newPreset);
    const preset = PRESET_RANGES.find(r => r.value === newPreset);
    if (preset) {
      const endTime = Date.now();
      const startTime = endTime - preset.ms;
      onChange({
        startTime,
        endTime,
        rangeType: 'preset',
        presetValue: newPreset,
      });
    }
  };

  const handleCustomChange = () => {
    if (startDateTime && endDateTime) {
      const startTime = new Date(startDateTime).getTime();
      const endTime = new Date(endDateTime).getTime();

      if (startTime < endTime) {
        onChange({
          startTime,
          endTime,
          rangeType: 'custom',
        });
      } else {
        console.warn('Invalid time range: start time must be before end time');
      }
    }
  };

  const handleModeChange = (newMode: 'preset' | 'custom') => {
    setMode(newMode);
    if (newMode === 'preset') {
      handlePresetChange(presetValue);
    } else {
      // Initialize with current time range (use local timezone)
      const now = Date.now();
      const oneHourAgo = now - 60 * 60 * 1000;
      setStartDateTime(formatDateTimeLocalFromTimestamp(oneHourAgo));
      setEndDateTime(formatDateTimeLocalFromTimestamp(now));
    }
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Mode Selector */}
      <div className="flex space-x-2">
        <button
          onClick={() => handleModeChange('preset')}
          className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            mode === 'preset'
              ? 'bg-indigo-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
          }`}
        >
          {t('timeRanges.quickSelect')}
        </button>
        <button
          onClick={() => handleModeChange('custom')}
          className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            mode === 'custom'
              ? 'bg-indigo-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
          }`}
        >
          {t('timeRanges.customRange')}
        </button>
      </div>

      {/* Preset Mode */}
      {mode === 'preset' && (
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4 lg:grid-cols-7">
          {PRESET_RANGES.map((range) => (
            <button
              key={range.value}
              onClick={() => handlePresetChange(range.value)}
              className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                presetValue === range.value
                  ? 'border-indigo-600 bg-indigo-50 text-indigo-700 dark:border-indigo-500 dark:bg-indigo-900/30 dark:text-indigo-300'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
      )}

      {/* Custom Mode */}
      {mode === 'custom' && (
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('timeRanges.startTime')}
              </label>
              <input
                type="datetime-local"
                value={startDateTime}
                onChange={(e) => setStartDateTime(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('timeRanges.endTime')}
              </label>
              <input
                type="datetime-local"
                value={endDateTime}
                onChange={(e) => setEndDateTime(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={handleCustomChange}
            disabled={!startDateTime || !endDateTime}
            className="w-full rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {t('timeRanges.apply')}
          </button>
        </div>
      )}

      {/* Current Range Display */}
      <div className="text-xs text-gray-500 dark:text-gray-400">
        {mode === 'preset' && value && (
          <span>
            {t('timeRanges.showingDataFrom')}{' '}
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {new Date(value.startTime).toLocaleString()}
            </span>{' '}
            {t('timeRanges.to')}{' '}
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {new Date(value.endTime).toLocaleString()}
            </span>
          </span>
        )}
        {mode === 'custom' && startDateTime && endDateTime && (
          <span>
            {t('timeRanges.customRangeLabel')}:{' '}
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {new Date(startDateTime).toLocaleString()}
            </span>{' '}
            {t('timeRanges.to')}{' '}
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {new Date(endDateTime).toLocaleString()}
            </span>
          </span>
        )}
      </div>
    </div>
  );
}
